import React, {FC, useEffect, useState} from 'react'
import axios from 'axios'
import {mondayApiService, MondaySaveConfigPayload} from '../services/mondayApi'

interface MondayConfigModalProps {
  show: boolean
  onClose: () => void
  onSuccess: () => void
}

const MondayConfigModal: FC<MondayConfigModalProps> = ({show, onClose, onSuccess}) => {
  const [apiToken, setApiToken] = useState('')
  const [hasExistingConfig, setHasExistingConfig] = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!show) return
    setError(null)
    setLoading(true)
    mondayApiService
      .getConfig()
      .then((config) => {
        if (config?.has_api_token) {
          setApiToken('')
          setHasExistingConfig(true)
        } else {
          setApiToken('')
          setHasExistingConfig(false)
        }
      })
      .catch(() => setError('Erro ao carregar configuração'))
      .finally(() => setLoading(false))
  }, [show])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const token = apiToken.trim()

    if (!hasExistingConfig && !token) {
      setError('Informe o API Token do Monday.com.')
      return
    }

    const payload: MondaySaveConfigPayload = {}
    if (token) {
      payload.api_token = token
    }

    if (!hasExistingConfig && !payload.api_token) {
      setError('Na primeira configuração, o API Token é obrigatório.')
      return
    }

    setSaving(true)
    setError(null)
    try {
      await mondayApiService.saveConfig(payload)
      onSuccess()
      onClose()
    } catch (err: unknown) {
      const message =
        axios.isAxiosError(err) && err.response?.data?.message
          ? String(err.response.data.message)
          : err instanceof Error
            ? err.message
            : 'Não foi possível salvar a configuração'
      setError(message)
    } finally {
      setSaving(false)
    }
  }

  if (!show) return null

  return (
    <div className='modal fade show d-block' style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
      <div className='modal-dialog modal-dialog-centered modal-lg'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>Configurar Monday.com</h5>
            <button type='button' className='btn-close' onClick={onClose} aria-label='Fechar' />
          </div>
          <form onSubmit={handleSubmit}>
            <div className='modal-body'>
              {loading ? (
                <div className='text-center py-5'>
                  <span className='spinner-border spinner-border-sm' />
                </div>
              ) : (
                <>
                  <div className='alert alert-info py-3 mb-4'>
                    Gere um Personal API Token no Monday.com em{' '}
                    <strong>Avatar → Developers → API token</strong>
                    (ou Avatar → Developers → My access tokens) e cole abaixo. Os webhooks por board são
                    registrados automaticamente ao salvar os mapeamentos.
                  </div>
                  {error && (
                    <div className='alert alert-danger' role='alert'>
                      {error}
                    </div>
                  )}
                  <div className='row g-3'>
                    <div className='col-12'>
                      <label className='form-label required'>API Token</label>
                      <input
                        type='password'
                        className='form-control form-control-sm'
                        value={apiToken}
                        onChange={(e) => setApiToken(e.target.value)}
                        placeholder={hasExistingConfig ? 'Deixe em branco para manter' : ''}
                        autoComplete='new-password'
                      />
                      {hasExistingConfig && (
                        <div className='form-text'>Um token já está configurado para esta instância.</div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className='modal-footer'>
              <button type='button' className='btn btn-light' onClick={onClose}>
                Cancelar
              </button>
              <button type='submit' className='btn btn-primary' disabled={loading || saving}>
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default MondayConfigModal
